import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import { useToastListContext } from "@contexts/ToastListContext";
import { useUserContext } from "@contexts/UserContext";
import { sendClarityEvent } from "@features/clarity/Clarity";
import { auth, firebase } from "@firebase-local/firebase";
import { logError } from "@service/ConsoleLoggerService";
import { requestFunction } from "@service/ServerlessFunctionsService";
import { UserSignInEvent } from "@user/AmplitudeUserEvents";
import { ClarityEvent } from "../clarity/ClarityEvents";
import { FIREBASE_NON_EMAIL_AUTH_PROVIDER_IDS } from "./components/UserAccountModal/UserAccountModalConstants";
import {
    sendUserVerificationEmail,
    setAmplitudeUserAccountProperties,
    signUserInSuccessWithAuthResult,
} from "./service/UserService";

type Props = {
    onSuccessRedirectUrl?: string;
};

export const useAppAuth = (props: Props) => {
    const context = useUserContext();
    const amplitudeService = AmplitudeService.getInstance();
    const { addToastNotification } = useToastListContext();

    const callbacks = {
        // User successfully signed in. Return type determines whether we continue
        // the redirect automatically or whether we leave that to developer to handle.
        signInSuccessWithAuthResult: function (authResult: firebase.auth.UserCredential) {
            // If the user is newly created, or has not verified email, always send the
            // verification request email.
            if (authResult.user && (authResult.additionalUserInfo?.isNewUser || !authResult.user.emailVerified)) {
                const uid: string = authResult.user.uid;
                context.setUser(authResult.user);

                // Set User ID for analytics
                // Note: we set user IDs here even though the user may not have verified their
                //  email so we can see if they get stuck/drop-off at this step.
                GA4Service.setUserId(uid);
                amplitudeService.setUserId(uid);

                // Set the user email in amplitude so we can easily reach out to users
                // from Segmentation charts we build.
                setAmplitudeUserAccountProperties(authResult.user.email, authResult.user.metadata.creationTime);

                // Log an event so we can track that the user is signing up for an account
                // via email to see how many get past the verification stage.
                GA4Service.logEvent(GA4Event.ACCOUNT_SIGN_IN, {
                    [GA4EventProperties.is_new_user]: authResult.additionalUserInfo?.isNewUser,
                });
                sendClarityEvent(ClarityEvent.SIGN_IN);
                if (authResult.additionalUserInfo?.isNewUser) {
                    GA4Service.logEvent(GA4Event.ACCOUNT_SIGN_UP, {
                        [GA4EventProperties.is_new_user]: authResult.additionalUserInfo?.isNewUser,
                    });
                    sendClarityEvent(ClarityEvent.SIGN_UP);
                }
                amplitudeService.logEvent(
                    new UserSignInEvent(true, authResult.additionalUserInfo?.isNewUser, authResult.user.emailVerified),
                );

                // Only send verification email if they used email to sign up.
                // Signing up via Google account is automatically verified.
                if (
                    !FIREBASE_NON_EMAIL_AUTH_PROVIDER_IDS.includes(authResult.additionalUserInfo.providerId) &&
                    !authResult.user.emailVerified
                ) {
                    sendUserVerificationEmail(authResult.user, props.onSuccessRedirectUrl);
                }

                // Return false so we don't redirect the user as part of Firebase code.
                return false;
            }

            // Subsequent Sign in (not a new user): email is verified or user signed in with google
            if (
                !authResult.additionalUserInfo.isNewUser &&
                (authResult.user.emailVerified ||
                    FIREBASE_NON_EMAIL_AUTH_PROVIDER_IDS.includes(authResult.user.providerId))
            ) {
                // Once the user email is verified, check to see if the user data is initialized.
                const handleVerifiedUserLogin = (needData: boolean) => {
                    if (!needData) {
                        // Set state to store the UID, so that we fetch the user's data and set the
                        // userData state var (if the user was just created, we still make a fetch call).
                        context.setUser(authResult.user); // updates the userData state var
                    }

                    // Set User ID for analytics
                    const uid: string = authResult.user.uid;

                    GA4Service.setUserId(uid);
                    amplitudeService.setUserId(uid);
                    setAmplitudeUserAccountProperties(authResult.user?.email, authResult.user?.metadata.creationTime);

                    // Log events
                    GA4Service.logEvent(GA4Event.ACCOUNT_SIGN_IN, {
                        [GA4EventProperties.is_new_user]: authResult.additionalUserInfo?.isNewUser,
                        [GA4EventProperties.account_signin_result.key]:
                            GA4EventProperties.account_signin_result.success,
                    });
                    amplitudeService.logEvent(
                        new UserSignInEvent(
                            true,
                            authResult.additionalUserInfo.isNewUser,
                            authResult.user.emailVerified,
                        ),
                    );
                    sendClarityEvent(ClarityEvent.SIGN_IN);
                };
                // Checks the DB for user data, and does the logic in the callback above
                // based on whether or not the data has been initialized.
                signUserInSuccessWithAuthResult(authResult.user.uid, handleVerifiedUserLogin);
            }

            // Return false so we don't redirect the user as part of Firebase code.
            return false;
        },
        signInFailure: function () {
            // Unset analytics tracking IDs and log event
            GA4Service.unsetUserId();
            amplitudeService.unsetUserId();

            // Log events
            GA4Service.logEvent(GA4Event.ACCOUNT_SIGN_IN_ERROR, {
                [GA4EventProperties.account_signin_result.key]: GA4EventProperties.account_signin_result.failure,
            });
            amplitudeService.logEvent(new UserSignInEvent(false));
        },
    };

    const signInWithGoogleOAuthCode = async (code: string): Promise<AuthResult> => {
        const { idToken } = await requestFunction("UserAccountFunctions-oauthGoogleExchangeCodeForToken", { code });
        const credentials = firebase.auth.GoogleAuthProvider.credential(idToken);
        try {
            const result = await auth.signInWithCredential(credentials);
            callbacks.signInSuccessWithAuthResult(result);
            return { success: true };
        } catch (e) {
            callbacks.signInFailure();
            return { success: false, error: "unknown", message: String(e) };
        }
    };

    const signInWithEmail = async (email: string, password: string): Promise<AuthResult> => {
        try {
            const result = await auth.signInWithEmailAndPassword(email, password);
            callbacks.signInSuccessWithAuthResult(result);
            return { success: true };
        } catch (e) {
            callbacks.signInFailure();
            if (e.code === "auth/user-not-found") {
                return { success: false, error: "user-not-found" };
            }
            if (e.code === "auth/wrong-password") {
                return { success: false, error: "wrong-password" };
            }
            return { success: false, error: "unknown", message: String(e) };
        }
    };

    const signUpWithEmail = async (email: string, password: string, displayName: string): Promise<AuthResult> => {
        try {
            const result = await auth.createUserWithEmailAndPassword(email, password);
            await result.user.updateProfile({ displayName });
            callbacks.signInSuccessWithAuthResult(result);
            return { success: true };
        } catch (e) {
            callbacks.signInFailure();
            if (e.code === "auth/email-already-in-use") {
                return { success: false, error: "email-already-in-use" };
            }
            return { success: false, error: "unknown", message: String(e) };
        }
    };

    const sendPasswordResetEmail = async (email: string) => {
        try {
            await auth.sendPasswordResetEmail(email);
            addToastNotification("Success", `Password reset email sent to ${email}`);
        } catch (e) {
            logError(e);
            addToastNotification("Error", "Failed to send password reset email. Please contact us.");
        }
    };

    return {
        signInWithGoogleOAuthCode,
        signInWithEmail,
        signUpWithEmail,
        sendPasswordResetEmail,
    };
};

export type AuthResult =
    | { success: true }
    | { success: false; error: "user-not-found" }
    | { success: false; error: "wrong-password" }
    | { success: false; error: "email-already-in-use" }
    | { success: false; error: "unknown"; message: string };
